/**
 * Mini in-browser preview of the branded invoice template.
 * Mirrors hub-backend/src/modules/invoice/invoice-pdf.renderer.ts.
 */

import { cn } from '@/lib/cn';

export interface InvoicePreviewProps {
  primaryColor: string;
  accentColor: string;
  highlightColor: string;
  companyName: string;
  companyEmail?: string | null;
  companyPhone?: string | null;
  companyWebsite?: string | null;
  taxId?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  postalCode?: string | null;
  signatoryName?: string | null;
  signatoryTitle?: string | null;
  invoiceFooterText?: string | null;
  bankName?: string | null;
  bankAccountName?: string | null;
  bankAccountNumber?: string | null;
  bankIfsc?: string | null;
  upiId?: string | null;
  paypalEmail?: string | null;
  paymentInstructions?: string | null;
  logoUrl?: string | null;
  signatureUrl?: string | null;
  className?: string;
  /** Switches the title + meta labels. Defaults to 'invoice'. */
  kind?: 'invoice' | 'quotation';
}

const sampleItems = [
  { description: 'Web Development — Discovery & Design', qty: 1, price: 1500 },
  { description: 'Implementation Sprint (40 hrs)', qty: 40, price: 75 },
  { description: 'QA & Deployment', qty: 1, price: 600 },
];

export function InvoiceTemplatePreview(props: InvoicePreviewProps) {
  const subtotal = sampleItems.reduce((s, it) => s + it.qty * it.price, 0);
  const tax = subtotal * 0.1;
  const total = subtotal + tax;

  const addrParts = [
    props.addressLine1,
    props.addressLine2,
    [props.city, props.state, props.postalCode].filter(Boolean).join(', '),
    props.country,
  ].filter(Boolean) as string[];
  const contactParts = [props.companyPhone, props.companyEmail, props.companyWebsite].filter(Boolean) as string[];

  const paymentLines: Array<[string, string]> = [];
  if (props.bankName) paymentLines.push(['Bank', props.bankName]);
  if (props.bankAccountName) paymentLines.push(['Account Name', props.bankAccountName]);
  if (props.bankAccountNumber) paymentLines.push(['Account No.', props.bankAccountNumber]);
  if (props.bankIfsc) paymentLines.push(['IFSC', props.bankIfsc]);
  if (props.upiId) paymentLines.push(['UPI', props.upiId]);
  if (props.paypalEmail) paymentLines.push(['PayPal', props.paypalEmail]);
  const hasPayment = paymentLines.length > 0 || !!props.paymentInstructions;

  return (
    <div
      className={cn('relative rounded-lg border bg-white shadow-sm overflow-hidden text-[10px] leading-snug', props.className)}
      style={{ aspectRatio: '0.707', maxWidth: 480, margin: '0 auto' }}
    >
      {/* Curved header — matches PDF: logo top-left in a fixed square, company info top-right.
          IMPORTANT: no background on this wrapper div — the SVG paints the dark area itself.
          A bg here would fill below the curve and flatten the wave. */}
      <div className="relative" style={{ color: '#fff' }}>
        <svg viewBox="0 0 480 155" className="block w-full" preserveAspectRatio="none" style={{ height: 155 }}>
          <rect x="0" y="0" width="480" height="110" fill={props.primaryColor} />
          {/* Closed path: top corners + curved bottom edge.
              Dip depth (~25) matches the PDF (~4.2% of page width). */}
          <path d="M 0 110 Q 240 160, 480 110 L 480 0 L 0 0 Z" fill={props.primaryColor} />
          <path d="M 0 112 Q 240 162, 480 112" stroke={props.accentColor} strokeWidth="1" fill="none" opacity="0.6" />
        </svg>
        <div className="absolute top-0 left-0 right-0 flex items-start px-4 pt-3 pb-2 gap-3">
          {/* Logo box — fixed square so different aspect ratios stay aligned.
              When no logo is uploaded the box is left intentionally blank;
              the company name still appears in the right block. */}
          <div className="flex items-center justify-center shrink-0" style={{ width: 56, height: 56 }}>
            {props.logoUrl && <img src={props.logoUrl} alt="logo" className="max-h-full max-w-full object-contain" />}
          </div>
          {/* Right-aligned company block — capped at ~half the preview so long
              addresses wrap within this column instead of bleeding over the logo. */}
          <div className="ml-auto text-right text-[8px] leading-snug min-w-0" style={{ maxWidth: '50%' }}>
            <div className="font-bold text-[10.5px] leading-tight">{props.companyName || 'Your Company'}</div>
            {addrParts.length > 0 && <div className="opacity-80 mt-1 break-words">{addrParts.join(', ')}</div>}
            {contactParts.length > 0 && <div className="opacity-80 mt-1.5">{contactParts.join('  •  ')}</div>}
            {props.taxId && <div className="opacity-60 mt-1">Tax ID: {props.taxId}</div>}
          </div>
        </div>
      </div>

      <div className="px-4 pt-3 pb-3">
        {/* Title */}
        <div className="font-extrabold text-2xl tracking-tight mb-3" style={{ color: props.accentColor }}>
          {props.kind === 'quotation' ? 'QUOTATION' : 'INVOICE'}
        </div>

        {/* Bill-to + meta */}
        <div className="grid grid-cols-2 gap-5 mb-3">
          <div>
            <div className="font-bold text-[7px] tracking-wider text-slate-400">{props.kind === 'quotation' ? 'QUOTE TO' : 'BILL TO'}</div>
            <div className="font-bold text-[11px] mt-0.5" style={{ color: props.primaryColor }}>Acme Industries Pvt Ltd</div>
            <div className="text-slate-600 text-[9px] mt-0.5">John Smith</div>
            <div className="text-slate-500 text-[8.5px] mt-0.5">billing@acme.com</div>
            <div className="text-slate-500 text-[8.5px]">214 Industrial Park</div>
            <div className="text-slate-500 text-[8.5px]">Bengaluru, KA 560001</div>
          </div>
          {/* Meta — labels and values both right-anchored close together; mirrors PDF */}
          <div className="text-right">
            {(props.kind === 'quotation'
              ? [
                  ['QUOTATION #', 'QUO-2026-000001'],
                  ['STATUS', 'SENT'],
                  ['ISSUE DATE', 'Apr 30, 2026'],
                  ['VALID UNTIL', 'May 30, 2026'],
                  ...(props.taxId ? [['TAX ID', props.taxId]] : []),
                ]
              : [
                  ['INVOICE #', 'INV-2026-000001'],
                  ['STATUS', 'SENT'],
                  ['ISSUE DATE', 'Apr 30, 2026'],
                  ['DUE DATE', 'May 15, 2026'],
                  ...(props.taxId ? [['TAX ID', props.taxId]] : []),
                ]
            ).map(([k, v]) => (
              <div key={k} className="flex items-baseline justify-end gap-3 py-[1px]">
                <span className="text-slate-400 font-bold text-[7px] tracking-wider">{k}</span>
                <span className="font-medium text-[8.5px] tabular-nums" style={{ color: props.primaryColor }}>{v}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Items — last column has built-in pr-2 right padding so the amounts don't kiss the edge */}
        <div className="rounded overflow-hidden">
          <div className="grid grid-cols-[18px_1fr_30px_55px_60px] pl-2 pr-3 py-1.5 text-white font-bold text-[8px] tracking-wider" style={{ background: props.accentColor }}>
            <div>#</div><div>DESCRIPTION</div><div className="text-right">QTY</div><div className="text-right">UNIT PRICE</div><div className="text-right">AMOUNT</div>
          </div>
          {sampleItems.map((it, i) => (
            <div key={i} className={cn('grid grid-cols-[18px_1fr_30px_55px_60px] pl-2 pr-3 py-1 text-[8.5px]', i % 2 ? 'bg-slate-50' : 'bg-white')} style={{ color: props.primaryColor }}>
              <div>{i + 1}</div>
              <div className="truncate">{it.description}</div>
              <div className="text-right">{it.qty.toFixed(2)}</div>
              <div className="text-right">${it.price.toFixed(2)}</div>
              <div className="text-right font-bold">${(it.qty * it.price).toFixed(2)}</div>
            </div>
          ))}
        </div>

        {/* Totals — values right-edge aligned with the table's AMOUNT column (matching pr-3) */}
        <div className="mt-2 ml-auto w-1/2 text-[8.5px]">
          <div className="space-y-0.5 pr-3">
            <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span style={{ color: props.primaryColor }}>${subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Tax (10%)</span><span style={{ color: props.primaryColor }}>${tax.toFixed(2)}</span></div>
          </div>
          {/* Grand Total band — full width of the totals column (= aligned with table band right edge); inside has pr-3 so the amount aligns with the AMOUNT column. */}
          <div className="rounded mt-1 pl-3 pr-3 py-1.5 flex justify-between items-center" style={{ background: props.accentColor }}>
            <span className="text-white font-bold tracking-wider text-[9px]">GRAND TOTAL</span>
            <span className="text-white font-bold text-[11px]">${total.toFixed(2)}</span>
          </div>
        </div>

        {/* Payment + signature */}
        <div className="grid grid-cols-2 gap-5 mt-4">
          <div>
            {hasPayment && <div className="text-[7px] font-bold text-slate-400 tracking-wider mb-1.5">PAYMENT INFO</div>}
            <div className="text-[8.5px] space-y-1.5 leading-relaxed" style={{ color: props.primaryColor }}>
              {paymentLines.map(([k, v]) => (
                <div key={k} className="flex">
                  <span className="text-slate-400 shrink-0" style={{ width: 70 }}>{k}:</span>
                  <span className="font-bold break-all">{v}</span>
                </div>
              ))}
              {props.paymentInstructions && <div className="text-slate-500 mt-2 text-[7.5px] leading-snug">{props.paymentInstructions}</div>}
            </div>
          </div>
          <div>
            <div className="ml-auto" style={{ width: '85%' }}>
              <div className="flex justify-end items-end" style={{ height: 50 }}>
                {props.signatureUrl
                  ? <img src={props.signatureUrl} alt="signature" className="max-h-12 max-w-full object-contain" />
                  : <div className="text-slate-300 italic text-[8px]">Signature</div>}
              </div>
              <div className="border-t mt-1" style={{ borderColor: props.accentColor }} />
              {props.signatoryName && <div className="font-bold text-[9px] mt-1 text-right" style={{ color: props.primaryColor }}>{props.signatoryName}</div>}
              {props.signatoryTitle && <div className="text-slate-500 text-[7.5px] text-right">{props.signatoryTitle}</div>}
            </div>
          </div>
        </div>
      </div>

      {/* Footer pinned to bottom */}
      <div className="absolute bottom-0 left-0 right-0 px-4 pb-2">
        <div className="border-t pt-1 text-center italic text-slate-500 text-[8.5px]" style={{ borderColor: props.accentColor }}>
          {props.invoiceFooterText || 'Thank you for your business!'}
        </div>
      </div>
    </div>
  );
}
